import { useEffect, useMemo, useState } from 'react'

import type { LotDebtItem } from '@/lib/api/product-lot-api'
import { useOpenLotDebts } from '@/hooks/use-lot-debt-query'
import { formatDateTime, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** Số bù cho từng khoản nợ, keyed theo lot_debt_id. */
export type DebtFillValue = Record<string, number>

const EPS = 1e-6

/** Số lượng trong kho là numeric(15,3) — làm tròn đúng độ chính xác đó. */
const QTY_DECIMALS = 1000

/**
 * Trừ số thực trong JS đẻ ra đuôi rác: 10 - 9.1 = 0.9000000000000004. Con số đó
 * vừa hiện ra ô nhập vừa đi thẳng vào payload, nên chặn ngay tại nguồn.
 */
function roundQty(n: number): number {
    return Math.round(n * QTY_DECIMALS) / QTY_DECIMALS
}

/**
 * Tổng số đã bù cho từng khoản nợ ở CÁC DÒNG LÔ KHÁC của cùng phiếu.
 *
 * Một phiếu được phép có nhiều dòng lô cùng trỏ vào một sản phẩm, khi đó mỗi
 * dòng có khối bù riêng nhưng cùng nhìn vào một danh sách đơn. Không biết nhau
 * thì cả hai cùng "Bù hết" cho một đơn, và màn hình báo tổng gấp đôi số thật
 * đi bù — backend có LEAST() nên không bù quá, nhưng người dùng bị nói dối.
 */
export function sumOtherRowsFill(
    rows: Array<{ kind?: string; debt_allocations?: DebtFillValue }>,
    currentIdx: number,
): DebtFillValue {
    const out: DebtFillValue = {}
    rows.forEach((row, i) => {
        if (i === currentIdx) return
        // Chỉ dòng "Lô mới" mới thực sự gửi phân bổ lên server. Đổi sang "Lô có
        // sẵn" KHÔNG xoá giá trị cũ khỏi form, nên nếu cộng cả dòng đó thì nó
        // chiếm chỗ oan và dòng khác không bù được phần lẽ ra còn.
        if (row.kind !== undefined && row.kind !== 'new') return
        for (const [id, qty] of Object.entries(row.debt_allocations ?? {})) {
            out[id] = (out[id] ?? 0) + qty
        }
    })
    return out
}

interface Props {
    /** Sản phẩm của dòng lô đang nhập. Chưa chọn ⇒ không hỏi backend. */
    productId: string | undefined
    /** Số lượng khai trên dòng lô này. */
    lotQuantity: number
    value: DebtFillValue
    onChange: (next: DebtFillValue) => void
    /** Phần các dòng lô KHÁC của cùng phiếu đã hứa cho từng khoản nợ. */
    usedByOtherRows?: DebtFillValue
}

/**
 * Khối "Bù đơn đang nợ hàng" trong một dòng lô của phiếu nhập (FD-372).
 *
 * Chỉ hiện khi sản phẩm của dòng lô đang có đơn nợ. Quan hệ là nhiều–nhiều: một
 * lô bù được nhiều đơn, một đơn nhận được nhiều lô — nên đây là bảng phân bổ chứ
 * không phải ô "chọn một đơn hàng".
 */
export function LotDebtFillSection({
    productId,
    lotQuantity,
    value,
    onChange,
    usedByOtherRows,
}: Props) {
    const { data: debts = [] } = useOpenLotDebts(productId)

    // Bản nháp dạng CHUỖI cho ô đang gõ. Ô số có kiểm soát mà ép qua Number()
    // mỗi lần gõ sẽ nuốt dấu chấm ngay khi người dùng mới gõ tới "1." — mà chè
    // bán theo kg nên số lẻ là chuyện thường. Cùng lý do khiến
    // lot-allocation-editor giữ `qty: string`, và ô vật tư cùng màn này dùng
    // register + setValueAs (input không kiểm soát).
    const [draft, setDraft] = useState<Record<string, string>>({})

    // Chưa nhập số lượng lô thì chưa có gì để bù — chỉ cho xem danh sách.
    const hasQuantity = lotQuantity > EPS

    // Đổi sản phẩm của dòng lô là đổi hẳn danh sách đơn nợ. Hai thứ phải theo
    // kịp, nếu không phân bổ của sản phẩm TRƯỚC vẫn đi theo phiếu và lúc duyệt
    // backend chặn vì lô bù sai sản phẩm — rollback cả phiếu.
    const debtIds = debts.map((d) => d.id).join(',')

    // Bỏ số đang gõ của sản phẩm cũ. Đặt state ngay trong render là pattern
    // React chính thức cho "reset state khi prop đổi" — rẻ hơn useEffect vì
    // không vẽ thừa một lượt với dữ liệu sai.
    const [draftFor, setDraftFor_] = useState(debtIds)
    if (draftFor !== debtIds) {
        setDraftFor_(debtIds)
        setDraft({})
    }

    // Dọn giá trị THẬT: phải tự chạy chứ không đợi thao tác, vì nhân viên có
    // thể đổi sản phẩm rồi bấm "Tạo phiếu" luôn mà không đụng vào khối này.
    useEffect(() => {
        // Danh sách rỗng còn có nghĩa "đang tải" hoặc "chưa chọn sản phẩm".
        // Dọn lúc đó là xoá oan phân bổ nhân viên vừa khai.
        if (debts.length === 0) return
        const known = new Set(debts.map((d) => d.id))
        if (!Object.keys(value).some((k) => !known.has(k))) return
        const next: DebtFillValue = {}
        for (const d of debts) {
            if (value[d.id] != null) next[d.id] = value[d.id]
        }
        onChange(next)
    }, [debts, value, onChange])

    /**
     * Số nợ mà DÒNG LÔ NÀY còn được bù: phần dòng khác đã hứa không còn là của
     * mình nữa. Mọi phép tính và mọi con số trên màn đều đi qua đây, để bảng
     * không nói một đằng payload một nẻo.
     */
    const owedHere = useMemo(() => {
        const out = new Map<string, number>()
        for (const d of debts) {
            out.set(
                d.id,
                Math.max(0, d.remaining - (usedByOtherRows?.[d.id] ?? 0)),
            )
        }
        return out
    }, [debts, usedByOtherRows])
    const owedOf = (id: string) => owedHere.get(id) ?? 0

    const applied = useMemo(
        () => debts.reduce((sum, d) => sum + (value[d.id] ?? 0), 0),
        [debts, value],
    )
    const leftForStock = lotQuantity - applied
    const stillOwing = debts.filter(
        (d) => owedOf(d.id) - (value[d.id] ?? 0) > EPS,
    )
    const stillOwingQty = stillOwing.reduce(
        (sum, d) => sum + (owedOf(d.id) - (value[d.id] ?? 0)),
        0,
    )
    // Gõ tay không bị chặn (rule repo: không khoá ô vì lỗi validate), nhưng bù
    // quá số một đơn đang nợ thì phải nói — backend clamp phần thừa nên nhân
    // viên sẽ không bao giờ thấy nó có tác dụng.
    const overDebt = debts.filter(
        (d) => (value[d.id] ?? 0) - owedOf(d.id) > EPS,
    )
    const unit = debts[0]?.product_unit ?? ''

    if (!productId || debts.length === 0) return null

    /**
     * Dựng lại phân bổ CHỈ từ các đơn đang hiển thị, thay vì spread `value` cũ.
     *
     * Đổi sản phẩm của dòng lô là đổi hẳn danh sách đơn nợ. Giữ lại key của sản
     * phẩm trước sẽ gửi lên một lot_debt_id không thuộc lô đang nhập, và lúc
     * DUYỆT phiếu backend chặn vì lô bù sai sản phẩm — rollback cả phiếu, với
     * một thông báo nhân viên không đoán được nguyên nhân.
     */
    const commit = (debtId: string, qty: number) => {
        const next: DebtFillValue = {}
        for (const d of debts) {
            next[d.id] = d.id === debtId ? qty : (value[d.id] ?? 0)
        }
        onChange(next)
    }

    /** Bản nháp cũng chỉ giữ đơn đang hiển thị, để ô không hiện số của sản phẩm
     *  trước trong khi giá trị thật đã bị dọn. */
    const writeDraft = (debtId: string, raw: string) => {
        setDraft((prev) => {
            const next: Record<string, string> = {}
            for (const d of debts) {
                if (d.id in prev) next[d.id] = prev[d.id]
            }
            next[debtId] = raw
            return next
        })
    }

    const patch = (debtId: string, raw: string) => {
        writeDraft(debtId, raw)
        const n = Number(raw)
        commit(debtId, Number.isFinite(n) && n > 0 ? n : 0)
    }

    /**
     * "Bù hết" cho MỘT đơn: lấy tối đa số còn nợ, nhưng không vượt phần lô nhập
     * chưa hứa cho đơn khác.
     *
     * Trừ đi số của CHÍNH dòng này khi tính phần còn lại — nếu không, bấm lần
     * thứ hai trên một dòng đã có số sẽ luôn ra 0.
     */
    const fillMax = (debt: LotDebtItem) => {
        const usedByOthers = debts.reduce(
            (sum, o) => (o.id === debt.id ? sum : sum + (value[o.id] ?? 0)),
            0,
        )
        const room = Math.max(0, lotQuantity - usedByOthers)
        const next = roundQty(Math.min(owedOf(debt.id), room))
        writeDraft(debt.id, String(next))
        commit(debt.id, next)
    }

    return (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-800/60 dark:bg-amber-950/30">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-semibold">
                    {m.lot_debt_fill_title()}
                </span>
                <span className="text-xs text-muted-foreground">
                    {m.lot_debt_fill_count({
                        orders: String(debts.length),
                        qty: formatQty(
                            debts.reduce((s, d) => s + owedOf(d.id), 0),
                        ),
                        unit,
                    })}
                </span>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">
                {m.lot_debt_fill_hint()}
            </p>

            {/* Bảng cuộn ngang trong container riêng để trang không cuộn ngang. */}
            <div className="overflow-x-auto rounded border bg-background">
                <table className="w-full min-w-[520px] text-sm">
                    <thead>
                        <tr className="border-b bg-muted/50 text-xs uppercase text-muted-foreground">
                            <th className="px-3 py-2 text-left font-medium">
                                {m.lot_debt_fill_col_order()}
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                                {m.lot_debt_fill_col_sold_at()}
                            </th>
                            <th className="px-3 py-2 text-right font-medium">
                                {m.lot_debt_fill_col_owing()}
                            </th>
                            <th className="px-3 py-2 text-right font-medium">
                                {m.lot_debt_fill_col_fill()}
                            </th>
                            <th className="px-3 py-2 text-right font-medium">
                                {m.lot_debt_fill_col_left()}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {debts.map((d) => {
                            const filled = value[d.id] ?? 0
                            const owed = owedOf(d.id)
                            const left = owed - filled
                            return (
                                <tr
                                    key={d.id}
                                    className="border-b last:border-0"
                                >
                                    <td className="px-3 py-2">
                                        <span className="font-mono text-xs">
                                            {d.order_code}
                                        </span>
                                        <span className="block text-xs text-muted-foreground">
                                            {d.customer_name}
                                        </span>
                                    </td>
                                    {/* KHÔNG cắt chuỗi ISO: chuỗi từ server
                                        theo UTC nên slice ra giờ lệch 7 tiếng
                                        so với giờ Việt Nam. */}
                                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                                        {formatDateTime(d.created_at)}
                                    </td>
                                    <td
                                        data-testid={`debt-owing-${d.id}`}
                                        className="px-3 py-2 text-right tabular-nums"
                                    >
                                        {formatQty(owed)} {d.product_unit}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {/* type="text" chứ KHÔNG phải "number":
                                            input số báo value rỗng ngay khi
                                            chuỗi còn dở dang ("1."), nên gõ số
                                            lẻ sẽ mất dấu chấm. inputMode giữ
                                            bàn phím số trên điện thoại. */}
                                        <div className="flex items-center justify-end gap-1">
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                aria-label={`bu-${d.id}`}
                                                value={
                                                    draft[d.id] ??
                                                    String(filled)
                                                }
                                                onChange={(e) =>
                                                    patch(d.id, e.target.value)
                                                }
                                                className="w-20 rounded border px-2 py-1 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-primary"
                                            />
                                            {/* Chỉ có nghĩa khi đã khai số
                                                lượng lô — chưa có gì để bù thì
                                                không bày nút. */}
                                            {hasQuantity && (
                                                <button
                                                    type="button"
                                                    aria-label={`bu-het-${d.id}`}
                                                    onClick={() => fillMax(d)}
                                                    className="whitespace-nowrap rounded border border-amber-300 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 focus:outline-none focus:ring-1 focus:ring-primary dark:border-amber-800/60 dark:text-amber-200 dark:hover:bg-amber-900/40"
                                                >
                                                    {m.lot_debt_fill_action_max()}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {left > EPS ? (
                                            <span className="font-medium text-amber-700 dark:text-amber-400">
                                                {formatQty(left)}
                                            </span>
                                        ) : (
                                            0
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            {hasQuantity && (
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <span data-testid="debt-fill-total">
                        {m.lot_debt_fill_total({
                            qty: formatQty(lotQuantity),
                            unit,
                        })}
                    </span>
                    <span data-testid="debt-fill-applied">
                        {m.lot_debt_fill_applied({
                            qty: formatQty(applied),
                            unit,
                        })}
                    </span>
                    <span
                        data-testid="debt-fill-left"
                        className="font-medium text-emerald-700 dark:text-emerald-400"
                    >
                        {m.lot_debt_fill_left({
                            qty: formatQty(Math.max(0, leftForStock)),
                            unit,
                        })}
                    </span>
                </div>
            )}

            {/* Bù quá số lô nhập: kho không có ngần ấy hàng để chia. */}
            {hasQuantity && leftForStock < -EPS && (
                <p
                    data-testid="debt-fill-over"
                    className="mt-1 text-xs text-destructive"
                >
                    {m.lot_debt_fill_over({
                        qty: formatQty(-leftForStock),
                        unit,
                    })}
                </p>
            )}

            {overDebt.length > 0 && (
                <p
                    data-testid="debt-fill-over-debt"
                    className="mt-1 text-xs text-destructive"
                >
                    {m.lot_debt_fill_over_debt({
                        orders: String(overDebt.length),
                    })}
                </p>
            )}

            {/* Nhập không đủ là chuyện thường của dây chuyền đóng gói — nêu rõ
                còn ai nợ bao nhiêu, KHÔNG chặn lưu phiếu. */}
            {hasQuantity && leftForStock >= -EPS && stillOwing.length > 0 && (
                <p
                    data-testid="debt-fill-remaining"
                    className="mt-1 text-xs text-amber-700 dark:text-amber-400"
                >
                    {m.lot_debt_fill_still_owing({
                        orders: String(stillOwing.length),
                        qty: formatQty(stillOwingQty),
                        unit,
                    })}
                </p>
            )}
        </div>
    )
}
